/**
 * Pure Outreach helpers (no UI imports).
 * Used by Outreach.slice to avoid import cycles with outreachConfig.
 */
import Moment from 'moment';

/** Platform date display — matches CampaignList formatFlightDate (e.g. "20 May, 2025"). */
export const formatOutreachDate = (timestamp) => {
  if (!timestamp) return '';
  const date = Moment(timestamp);
  return date.isValid() ? date.format('D MMM, YYYY') : '';
};

/** Document "sent on" label using the platform date format (e.g. "Sent 20 May, 2025"). */
export const formatOutreachSentDate = (timestamp) => {
  const formatted = formatOutreachDate(timestamp);
  return formatted ? `Sent ${formatted}` : '';
};

/** Collapse duplicate document recipients (same person can appear multiple times in raw API data). */
export const uniqueOutreachRecipients = (recipients) => {
  if (!Array.isArray(recipients)) return [];
  const seen = new Set();
  return recipients.filter((recipient) => {
    const key = String(
      recipient.creatorReportDetailsId ?? recipient.username ?? recipient.displayName ?? ''
    );
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const emptyMessageCenterOutreachData = {
  contactDetails: [],
  emails: [],
  outreachId: null,
  emailCount: 0,
  senderInfo: null,
};

// ── Message Center v2 (realtime API) helpers ────────────────────────────────

const stripHtmlTags = (html) => {
  if (!html) return '';
  return String(html)
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * Map a /message-center/threads row into the creator-compatible shape the
 * existing Message Center components (sidebar, chat header, toolbar, agreement
 * modal) already consume. Raw API fields are spread first so nothing is lost.
 */
export const mapMessageCenterThreadRow = (row = {}) => {
  const creator = row.creator || {};
  const lastMessage = row.lastMessage || null;
  const previewText = lastMessage?.preview ? stripHtmlTags(lastMessage.preview) : '';
  const isSent = lastMessage?.direction === 'sent';
  const previewWithPrefix = previewText && isSent ? `You: ${previewText}` : previewText;

  return {
    ...row,
    id: String(row.outreachId ?? row.creatorReportDetailsId ?? ''),
    outreachId: row.outreachId ?? null,
    creatorReportDetailsId: row.creatorReportDetailsId ?? null,
    builderRequestId: row.builderRequestId ?? creator.builderRequestId ?? null,
    segmentId: row.segmentId ?? creator.segmentId ?? null,
    name: creator.name ?? '',
    username: creator.username ?? '',
    socialNetwork: creator.network ?? '',
    imageURL: creator.avatar ?? '',
    status: row.creatorStatus ?? null,
    notOutreached: Boolean(row.notOutreached),
    unreadCount: Number(row.unreadCount) || 0,
    messagesCount: Number(row.messagesCount) || 0,
    lastActivityAt: row.lastActivityAt ?? lastMessage?.at ?? null,
    lastMessageId: lastMessage?.id ?? null,
    lastMessageDirection: lastMessage?.direction ?? null,
    // Compatibility with getConversationListSecondRowMeta / sort helpers:
    lastMsgHTML: previewWithPrefix ? `<p>${previewWithPrefix}</p>` : '',
    lastMsgCreatedDate: lastMessage?.at ?? row.lastActivityAt ?? null,
    creatorLastReply: lastMessage?.direction === 'received' ? 1 : 0,
    outreachInitiated: row.notOutreached ? '0' : '1',
  };
};

/** Merge /campaign/fetchCreators fields needed by updateStatus + compose into a thread row. */
export const mergeCampaignCreatorIntoMcThread = (threadRow, campaignCreator = {}) => {
  if (!campaignCreator || !threadRow) return threadRow;
  return {
    ...threadRow,
    builderRequestId: threadRow.builderRequestId ?? campaignCreator.builderRequestId ?? null,
    segmentId: threadRow.segmentId ?? campaignCreator.segmentId ?? null,
    contactDetails: threadRow.contactDetails?.length
      ? threadRow.contactDetails
      : campaignCreator.contactDetails,
    customContactEmails: threadRow.customContactEmails?.length
      ? threadRow.customContactEmails
      : campaignCreator.customContactEmails,
  };
};

export const enrichMcThreadsWithCampaignCreators = (threads, campaignCreators = []) => {
  const byCrd = new Map(campaignCreators.map((c) => [String(c.creatorReportDetailsId), c]));
  return threads.map((row) => {
    const match = byCrd.get(String(row.creatorReportDetailsId));
    return match ? mergeCampaignCreatorIntoMcThread(row, match) : row;
  });
};

export const patchMcThreadCreatorFields = (threads, creatorReportDetailsId, patch = {}) => {
  const targetId = String(creatorReportDetailsId);
  const hasPatch = Object.values(patch).some((v) => v != null && v !== '');
  if (!hasPatch) return threads;

  return threads.map((row) => {
    if (String(row.creatorReportDetailsId) !== targetId) return row;
    return {
      ...row,
      builderRequestId: patch.builderRequestId ?? row.builderRequestId,
      segmentId: patch.segmentId ?? row.segmentId,
    };
  });
};

/**
 * Map a /message-center/threads/{id}/messages item into a chat-bubble shape.
 * `direction`: 'out' (our side) | 'in' (influencer). Display order is by time,
 * sync logic uses ids (kept on `messageId`).
 */
export const mapMessageCenterMessage = (msg = {}, creatorName = 'Influencer') => {
  const isOut = msg.direction === 'sent';
  const timestamp = msg.sentAt || msg.receivedAt || msg.createdAt || null;
  return {
    id: String(msg.id),
    messageId: Number(msg.id),
    direction: isOut ? 'out' : 'in',
    sender: isOut ? 'You' : creatorName,
    time: timestamp,
    dateTime: timestamp,
    bodyHTML: msg.bodyHtml || '',
    subject: msg.subject || '',
    status: msg.status || null,
    // Queued (Gmail) sends report their outcome asynchronously: `platform` tells
    // us whether a status is meaningful, `failureReason` is the copy to show.
    platform: msg.platform || null,
    failureReason: msg.failureReason || null,
    from: msg.from || null,
    to: Array.isArray(msg.to) ? msg.to : [],
    cc: Array.isArray(msg.cc) ? msg.cc : [],
    bcc: Array.isArray(msg.bcc) ? msg.bcc : [],
    attachments: Array.isArray(msg.attachments) ? msg.attachments : [],
  };
};

/**
 * Delivery state of a message for the chat UI: 'sending' | 'failed' | null.
 * Only queued (Gmail) sends have a meaningful in-between status (see
 * FE_GMAIL_QUEUED_SEND.md section 3). A row that carries no `platform` is still
 * honoured so the chip works if the backend omits it, but an Outlook/default row
 * with a stale legacy status never shows one.
 */
export const getMessageDeliveryState = (msg = {}) => {
  if (msg.platform && msg.platform !== 'gmail') return null;
  if (msg.status === 'init' || msg.status === 'pending') return 'sending';
  if (msg.status === 'failed') return 'failed';
  return null;
};

/**
 * Backends vary in how they label the OAuth provider (casing, 'google' vs
 * 'gmail'). Canonicalize so the exact-match `platform === 'gmail'` checks across
 * the app (sender icons, connect menus, Settings cards) recognize the connection
 * and so the value echoed back as senderInfo matches the routing contract.
 */
export const normalizeSenderPlatform = (platform) => {
  const p = String(platform || '')
    .trim()
    .toLowerCase();
  if (p === 'gmail' || p === 'google') return 'gmail';
  if (p === 'outlook') return 'outlook';
  return p || 'default';
};

/** Is this sender a Gmail connection? (A 200 then means "queued", not "delivered".) */
export const isGmailSender = (senderInfo) =>
  normalizeSenderPlatform(senderInfo?.platform) === 'gmail';

/**
 * Gmail sender row the backend flagged (`needsReconnect`): granted without
 * send/read permission, revoked or expired. Sends from it fail until the user
 * reconnects that mailbox. Missing flag (default row, Outlook) counts as false.
 */
export const needsGmailReconnect = (sender) =>
  isGmailSender(sender) && sender?.needsReconnect === true;

export const GMAIL_RECONNECT_LABEL = 'Reconnect needed';

export const getGmailReconnectSendError = (sender) =>
  `Reconnect ${sender?.email || 'this Gmail account'} before sending from it.`;

/** Extract a bare email from a `Name <email@x.com>` or plain `email@x.com` string. */
export const extractEmail = (value) => {
  const match = String(value || '').match(/<([^>]+)>/);
  return (match ? match[1] : value || '').trim();
};

/**
 * The mailbox id a connection row sends from — the same id stored in a
 * thread's `senderInfo.oauthInfoId`. Rows also carry `OAuthInfoDetailsId`
 * (the account↔mailbox link, used only to disconnect); the two almost never
 * match, so never fall back to it here.
 */
export const getSenderConnectionId = (conn) => conn?.oauthInfoId;

/**
 * Mailbox a thread was sent from, lower-cased. In order: the stored sender's
 * email when the backend provides it; the connection row the thread's
 * oauthInfoId points at (those rows carry both the id and the address); else
 * the `from` of the latest outbound message.
 *
 * All three can miss — /outreach/init returns senderInfo without an `email`,
 * and `from` is the INBOUND sender by this API's convention, so outbound rows
 * are null. Callers must handle null rather than assume a mailbox.
 */
export const getThreadSenderEmail = (senderInfo, messages = [], connections = []) => {
  if (senderInfo?.email) return extractEmail(senderInfo.email).toLowerCase() || null;

  if (senderInfo?.oauthInfoId != null && Array.isArray(connections)) {
    const owner = connections.find(
      (conn) => getSenderConnectionId(conn) === senderInfo.oauthInfoId
    );
    if (owner?.email) return extractEmail(owner.email).toLowerCase() || null;
  }

  const latestOut = [...messages]
    .sort((a, b) => (b.messageId || 0) - (a.messageId || 0))
    .find((m) => m.direction === 'out' && m.from);
  return latestOut ? extractEmail(latestOut.from).toLowerCase() || null : null;
};

/**
 * Derive a reply target from a thread's messages (newest-first preference):
 * latest inbound `from` (the creator), else the latest outbound `to`.
 * Returns { to: string[], subject: string }.
 */
export const deriveMessageCenterReplyTarget = (messages = []) => {
  const byTimeDesc = [...messages].sort((a, b) => (b.messageId || 0) - (a.messageId || 0));
  const inbound = byTimeDesc.find((m) => m.direction === 'in' && m.from);
  const outbound = byTimeDesc.find((m) => m.direction === 'out' && (m.to || []).length > 0);
  const latest = byTimeDesc[0] || {};

  let to = [];
  if (inbound?.from) to = [inbound.from];
  else if (outbound?.to?.length) to = outbound.to;

  const baseSubject = latest.subject || '';
  const subject = baseSubject && !/^re:/i.test(baseSubject) ? `Re: ${baseSubject}` : baseSubject;
  return { to, subject };
};

/**
 * Recipient emails (to + cc + bcc) used in the outreach thread. These are
 * identical across every outreach email in the thread, so we read them from a
 * single representative message — the most recent outbound message that has
 * recipients — rather than scanning the whole thread. Returns bare emails.
 */
export const getThreadRecipientEmails = (messages = []) => {
  const byTimeDesc = [...messages].sort((a, b) => (b.messageId || 0) - (a.messageId || 0));
  const outbound = byTimeDesc.find((m) => m.direction === 'out' && (m.to || []).length > 0);
  if (!outbound) return [];
  return [...(outbound.to || []), ...(outbound.cc || []), ...(outbound.bcc || [])]
    .map(extractEmail)
    .filter(Boolean);
};

// ── Deliverable content types (server-owned via /deliverables/content-types) ─

export const CONTENT_TYPE_KEY_DELIMITER = '::';

/**
 * A content type is only unambiguous together with its network (e.g. instagram
 * "posts" vs youtube "posts"), so the dropdown option value is a `network::type`
 * composite. This keeps options unique and lets the modal recover the network of
 * the user's choice when building the payload.
 */
export const makeContentTypeKey = (network, type) =>
  `${network || ''}${CONTENT_TYPE_KEY_DELIMITER}${type || ''}`;

const capitalizeContentType = (type) => {
  const base = String(type || '').split('_')[0];
  return base ? base.charAt(0).toUpperCase() + base.slice(1) : '';
};

/**
 * Resolve a stored content type to its human label using the server-owned content
 * types. The API `label` is already fully qualified (e.g. "TikTok Video"), so it is
 * returned as-is — callers must NOT prepend the network again.
 *
 * `network` disambiguates same-type/different-network codes (instagram "posts" vs
 * youtube "posts"). Handles indexed codes ("videos_1" → base "videos"). When the
 * type isn't in the API list, falls back to a "<Network> <Type>" capitalized string
 * so legacy/unknown codes still render with context.
 */
export const getDeliverableContentTypeLabel = (contentTypes, type, network) => {
  if (!type) return '';
  const base = String(type)
    .split('_')[0]
    .toLowerCase();
  const net = network ? String(network).toLowerCase() : '';
  const list = Array.isArray(contentTypes) ? contentTypes : [];

  // Prefer the exact network+type match (e.g. real "twitter.posts" → "X (Twitter)
  // Post"). If the saved network+type combo isn't a real content type (legacy/bad
  // data like "tiktok.posts"), fall back to any content type with that base type so
  // it still resolves to a real label instead of a synthetic "<Network> <Type>".
  const exactMatch = net
    ? list.find(
        (ct) => String(ct.type).toLowerCase() === base && String(ct.network).toLowerCase() === net
      )
    : null;
  const typeMatch = exactMatch || list.find((ct) => String(ct.type).toLowerCase() === base);
  if (typeMatch?.label) return typeMatch.label;

  const typeLabel = capitalizeContentType(base);
  const networkLabel = net ? capitalizeContentType(net) : '';
  return networkLabel ? `${networkLabel} ${typeLabel}` : typeLabel;
};

/**
 * Build Select options from the server-owned deliverable content types. Every
 * (network, type) pair becomes a distinct option keyed by a `network::type`
 * composite value and tagged with its `network`/`type`. `extraRows` ensures any
 * already-saved {network, type} combos the API no longer returns still render as
 * selectable options (backwards compatibility). Result is de-duplicated by value.
 *
 * Network labelling is left to the caller (UI layer) to avoid an import cycle.
 */
export const getDeliverableContentTypeOptions = (contentTypes, extraRows = []) => {
  const list = Array.isArray(contentTypes) ? contentTypes : [];

  const fromApi = list
    .filter((ct) => ct && ct.type)
    .map((ct) => ({
      value: makeContentTypeKey(ct.network, ct.type),
      label: ct.label || capitalizeContentType(ct.type),
      network: String(ct.network || ''),
      type: String(ct.type),
    }));

  const extras = (Array.isArray(extraRows) ? extraRows : [])
    .filter((row) => row && row.type)
    .map((row) => ({
      value: makeContentTypeKey(row.network, row.type),
      label: getDeliverableContentTypeLabel(list, row.type, row.network),
      network: String(row.network || ''),
      type: String(row.type),
    }));

  const seen = new Set();
  return [...fromApi, ...extras].filter((opt) => {
    if (seen.has(opt.value)) return false;
    seen.add(opt.value);
    return true;
  });
};

/** Sort chat messages ascending by time (display order, per guide §4.2). */
export const sortMessageCenterMessages = (messages = []) =>
  [...messages].sort((a, b) => {
    // A queued message can arrive before the backend stamps sentAt. No time means
    // "not sent yet", so it belongs at the bottom of the thread, not the top.
    const ta = a.time ? new Date(a.time).getTime() : Number.MAX_SAFE_INTEGER;
    const tb = b.time ? new Date(b.time).getTime() : Number.MAX_SAFE_INTEGER;
    if (ta !== tb) return ta - tb;
    return (a.messageId || 0) - (b.messageId || 0);
  });

/** Merge incoming messages into existing list, de-duping by id. */
export const mergeMessageCenterMessages = (existing = [], incoming = []) => {
  const byId = new Map();
  [...existing, ...incoming].forEach((m) => {
    byId.set(String(m.id), m);
  });
  return sortMessageCenterMessages(Array.from(byId.values()));
};

export const normalizeMessageCenterOutreachResponse = (resp) => {
  const source =
    resp?.data != null && typeof resp.data === 'object' && !Array.isArray(resp.data)
      ? { ...resp, ...resp.data }
      : { ...(resp || {}) };

  const outreachData = { ...source };
  const { contactDetails = [] } = source;

  if (contactDetails == null) {
    outreachData.contactDetails = [];
  }

  if (!contactDetails || contactDetails.length === 0) {
    outreachData.emailTo = [];
  } else {
    const emailToObj = contactDetails.find((contact) => contact.type === 'email');
    outreachData.emailTo = emailToObj ? [emailToObj.value] : [];
  }

  if (!Array.isArray(outreachData.emails)) {
    outreachData.emails = [];
  }

  return outreachData;
};
